import { useTranslation } from 'react-i18next';
import { BottomSheet } from './BottomSheet';
import { lockDaysLeft, lockProgress, type StakePosition } from '../../lib/staking';
import { fmtDate, type PositionMeta } from './stakeMeta';

type Props = {
  position: StakePosition | null;
  meta: PositionMeta | null;
  onClose: () => void;
};

const DAY_MS = 86_400_000;

/** Shown instead of the withdraw sheet while a `lock` position is still locked. */
export function StakeLockSheet({ position, meta, onClose }: Props) {
  const { t, i18n } = useTranslation();

  // Prefer the API date; fall back so the sheet never opens empty.
  const lockEndsAt =
    position && meta
      ? (position.lockEndsAt ??
        new Date(
          new Date(position.openedAt).getTime() + meta.termDays * DAY_MS,
        ).toISOString())
      : null;
  const lockPos =
    position && lockEndsAt ? { ...position, lockEndsAt } : position;

  const open = !!lockPos && !!meta && !!lockEndsAt;
  const percent = lockPos ? Math.round(lockProgress(lockPos) * 100) : 0;
  const daysLeft = lockPos ? lockDaysLeft(lockPos) : 0;

  return (
    <BottomSheet open={open} onClose={onClose}>
      {lockPos && meta && lockEndsAt && (
        <div className="pb-2">
          <div className="pt-1 text-center">
            <span className="inline-flex h-[60px] w-[60px] items-center justify-center rounded-full bg-black/[0.05]">
              <svg
                width="27"
                height="27"
                fill="none"
                stroke="#6d6d80"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <rect x="4.5" y="10.5" width="15" height="10" rx="2.6" />
                <path d="M8 10.5V7.6a4 4 0 0 1 8 0v2.9" />
              </svg>
            </span>
            <div className="mt-3 text-[19px] font-semibold tracking-[-0.4px] text-ink">
              {t('stake.lockTitle')}
            </div>
            <div className="mt-1 text-[13px] text-muted">
              {meta.name} · #{lockPos.id}
            </div>
          </div>

          <div className="mt-4 text-center">
            <div className="text-[12px] text-muted">{t('stake.lockEndsOn')}</div>
            <div className="mt-0.5 text-[26px] font-semibold tracking-[-0.8px] text-ink">
              {fmtDate(lockEndsAt, i18n.language)}
            </div>
          </div>

          <div className="mb-2 mt-3.5 h-[7px] overflow-hidden rounded-[4px] bg-black/[0.06]">
            <i
              className="block h-full rounded-[4px]"
              style={{ width: `${percent}%`, background: meta.accent }}
            />
          </div>
          <div className="flex justify-between text-[12px] text-muted">
            <span>{t('stake.lockPassed', { percent })}</span>
            <span>{t('stake.lockLeft', { count: daysLeft })}</span>
          </div>

          <p className="mt-3.5 text-[12.5px] leading-[1.55] text-muted">
            {t('stake.lockNote', { rate: meta.rate })}
          </p>

          <button
            type="button"
            onClick={onClose}
            className="mt-4 h-12 w-full rounded-[15px] bg-ink text-[15px] font-semibold text-white active:opacity-90"
          >
            {t('stake.gotIt')}
          </button>
        </div>
      )}
    </BottomSheet>
  );
}
